//
// Very minimal mock so we can assert different things about the logger
// during test runs
//
// -- Usage
//
// Mock the logger module use the *mock-require* module, or any other mocking module that
// does a simlar thing
//
// ``` testSetup.js
// const mock = require('mock-require')
// const loggerMock = require('@adc/logger').mock
//
// mock('@adc/logger', loggerMock)
// ```
//
// Place the *resetBeforeEach* in your mocha describe block to suppress and track logger output for
// the whole block.
//
// ``` test.js
// describe('test with logger assertions', () => {
//   loggerMock.resetBeforeEach()
// })
// ```
//
let state = 0;

// Wrap this in a function so we don't export an initial object that we then
// replace later. We want to change the underlying object after each test
const getState = () => state;
const trapCall = level => {
  return (...args) => {
    state.callCount++;
    state[level].callCount++;
    state[level].calls.push(args);
  };
};
const lastCall = level => {
  return () => {
    const calls = state[level].calls;
    return calls[calls.length - 1];
  };
};
const resetState = () => {
  state = {
    callCount: 0
  };
  ['error', 'info', 'debug', 'warn'].forEach(level => {
    state[level] = {
      calls: [],
      callCount: 0,
      lastCall: lastCall(level)
    };
  });
};

// Call once to set
resetState();
const resetBeforeEach = () => beforeEach(resetState);
const noop = () => {};
const mock = {
  state: getState,
  resetBeforeEach,
  error: trapCall('error'),
  info: trapCall('info'),
  warn: trapCall('warn'),
  debug: trapCall('debug'),
  middleware: opts => (req, res, next) => next(),
  forModule: str => mock,
  // NOTE: This is fake, just to match the interface. The main reason for this is because we don't presently
  // have any formal specification of what adapters are compatible with which env. The `DatadogAdapter` is only
  // applicable for a node environment, and should never be used in the browser.
  outputAdapters: {},
  configuration: {
    get: noop,
    set: noop,
    update: noop,
    reset: noop
  },
  addSanitizedKeys: noop,
  flushAll: noop,
  stream: {
    write: (...args) => mock.warn(...args)
  },
  RequestLogger: function () {
    this.request = () => {};
    this.response = () => {};
  }
};
module.exports = mock;